// public/js/features/pois/poi-overlay-calibrator.js
// Two-phase live calibration for a POI map overlay (docs/poi-custom-maps.md):
//   1) place numbered points on a ZOOM/PAN-able image,
//   2) drag the matching numbered pins on the MAP to the real-world spots,
// with a live overlay preview + opacity slider so the fit is visible while you
// work. Used for both new overlays and re-calibrating existing ones.
import { affineFromPoints, cornersFromAffine, ensureOverlaysAtBottom, imageDataUrl } from './poi-overlay-layer.js';
import { uploadOverlayImage } from './api.js';

const PREVIEW_SRC = 'poi-ov-calib-src';
const PREVIEW_LAYER = 'poi-ov-calib';   // starts with poi-ov- → ordered like an overlay
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// { map, poiId, existing|null, newImage|null, hide(), show(), onDone(overlay|null) }
export async function openOverlayCalibrator({ map, poiId, existing, newImage, hide, show, onDone }) {
  if (!map || typeof maplibregl === 'undefined') { window.showToast?.('Open the Map page and try again', 'warning'); onDone?.(null); return; }

  // Resolve the image + metadata.
  let dataURL, width, height, name, contentType = null, imageKey = null, editingId = null;
  if (existing) {
    editingId = existing.id; name = existing.name || 'Overlay'; imageKey = existing.imageKey;
    width = existing.width; height = existing.height;
    window.showToast?.('Loading overlay image…', 'info');
    try { dataURL = await imageDataUrl(existing.imageKey); }
    catch (e) { window.showToast?.('Could not load the overlay image', 'danger'); onDone?.(null); return; }
  } else if (newImage) {
    dataURL = newImage.dataURL; width = newImage.width; height = newImage.height;
    name = newImage.name || 'Overlay'; contentType = newImage.contentType;
  } else { onDone?.(null); return; }

  const img = new Image();
  try { await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataURL; }); }
  catch { window.showToast?.('Could not display the image', 'danger'); onDone?.(null); return; }

  const state = { points: [], opacity: existing ? (existing.opacityDefault ?? 0.7) : 0.7 };

  // Hide the real overlay layer while calibrating so it doesn't double the preview.
  if (editingId) { try { map.setLayoutProperty(`poi-ov-${editingId}`, 'visibility', 'none'); } catch (e) {} }
  hide?.(); // free the map (close the POI dialog)

  // ── panel ────────────────────────────────────────────────────────────────
  const panel = document.createElement('div');
  panel.className = 'ov-cal-panel';
  panel.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h6 class="mb-0">${existing ? 'Edit overlay — recalibrate' : 'New overlay'}</h6>
      <button type="button" class="btn-close" id="ovc-x" aria-label="Cancel"></button>
    </div>
    <input class="form-control form-control-sm mb-2" id="ovc-name" value="${esc(name)}" placeholder="Overlay name">
    <div class="text-muted mb-1" style="font-size:.72rem;">
      1) Click matching features on the image (wheel = zoom, drag = pan).<br>
      2) Drag each numbered pin on the map onto the same real spot (zoom the map freely).
    </div>
    <canvas id="ovc-canvas" class="ov-cal-canvas"></canvas>
    <div class="d-flex align-items-center gap-2 my-2">
      <span class="text-muted" style="font-size:.72rem;">Overlay opacity</span>
      <input type="range" min="0" max="1" step="0.05" value="${state.opacity}" class="form-range" id="ovc-op" style="flex:1;">
      <button type="button" class="btn btn-sm btn-outline-secondary" id="ovc-fit" title="Fit image"><i class="fa-solid fa-expand"></i></button>
    </div>
    <ol class="small ps-3 mb-2" id="ovc-list" style="max-height:120px;overflow:auto;"></ol>
    <div class="d-flex gap-2">
      <button type="button" class="btn btn-sm btn-success" id="ovc-done">Save</button>
      <button type="button" class="btn btn-sm btn-outline-secondary" id="ovc-cancel">Cancel</button>
    </div>`;
  document.body.appendChild(panel);

  const canvas = panel.querySelector('#ovc-canvas');
  const listEl = panel.querySelector('#ovc-list');
  const nameInput = panel.querySelector('#ovc-name');
  const doneBtn = panel.querySelector('#ovc-done');
  canvas.width = 336; canvas.height = 250;
  const view = { scale: 1, ox: 0, oy: 0 };
  const fit = () => {
    const s = Math.min(canvas.width / width, canvas.height / height);
    view.scale = s; view.ox = (canvas.width - width * s) / 2; view.oy = (canvas.height - height * s) / 2;
  };
  fit();

  function draw() {
    const c = canvas.getContext('2d');
    c.setTransform(1, 0, 0, 1, 0, 0); c.clearRect(0, 0, canvas.width, canvas.height);
    c.fillStyle = '#eef2f7'; c.fillRect(0, 0, canvas.width, canvas.height);
    c.drawImage(img, view.ox, view.oy, width * view.scale, height * view.scale);
    state.points.forEach((p, i) => {
      const x = view.ox + p.px * view.scale, y = view.oy + p.py * view.scale;
      c.beginPath(); c.arc(x, y, 9, 0, Math.PI * 2); c.fillStyle = '#0d6efd'; c.fill();
      c.strokeStyle = '#fff'; c.lineWidth = 2; c.stroke();
      c.fillStyle = '#fff'; c.font = 'bold 11px sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(String(i + 1), x, y);
    });
  }

  function renumber() {
    state.points.forEach((p, i) => { const el = p.marker.getElement(); if (el) el.textContent = String(i + 1); });
  }
  function renderList() {
    listEl.innerHTML = state.points.length
      ? state.points.map((p, i) => `<li class="d-flex align-items-center gap-2"><span class="flex-grow-1">Point ${i + 1}</span>
          <button type="button" class="btn btn-sm btn-outline-danger py-0 px-1 ovc-del" data-i="${i}" title="Remove"><i class="fa-solid fa-xmark"></i></button></li>`).join('')
      : '<li class="text-muted">No points yet — click the image.</li>';
    listEl.querySelectorAll('.ovc-del').forEach(b => b.addEventListener('click', () => removePoint(Number(b.dataset.i))));
  }

  // ── live preview on the map ───────────────────────────────────────────────
  function corners() {
    const pts = state.points.map(p => { const ll = p.marker.getLngLat(); return { px: p.px, py: p.py, lng: ll.lng, lat: ll.lat }; });
    if (pts.length < 3) return null;
    const af = affineFromPoints(pts);
    return af ? cornersFromAffine(af, width, height) : null;
  }
  function updatePreview() {
    const cs = corners();
    if (!cs) {
      try { if (map.getLayer(PREVIEW_LAYER)) map.removeLayer(PREVIEW_LAYER); } catch (e) {}
      try { if (map.getSource(PREVIEW_SRC)) map.removeSource(PREVIEW_SRC); } catch (e) {}
      return;
    }
    const coords = cs.map(c => [c[0], c[1]]);
    if (map.getSource(PREVIEW_SRC)) {
      try { map.getSource(PREVIEW_SRC).setCoordinates(coords); } catch (e) {}
      try { map.setPaintProperty(PREVIEW_LAYER, 'raster-opacity', state.opacity); } catch (e) {}
    } else {
      map.addSource(PREVIEW_SRC, { type: 'image', url: dataURL, coordinates: coords });
      map.addLayer({ id: PREVIEW_LAYER, type: 'raster', source: PREVIEW_SRC, paint: { 'raster-opacity': state.opacity, 'raster-fade-duration': 0 } });
      ensureOverlaysAtBottom(map);
    }
  }

  // ── points ────────────────────────────────────────────────────────────────
  function makeMarkerEl(n) { const d = document.createElement('div'); d.className = 'ov-cal-marker'; d.textContent = String(n); return d; }
  function addPoint(px, py) {
    // Place the pin at the current map centre; the user drags it into place.
    const marker = new maplibregl.Marker({ element: makeMarkerEl(state.points.length + 1), draggable: true })
      .setLngLat(map.getCenter()).addTo(map);
    marker.on('drag', updatePreview);
    marker.on('dragend', updatePreview);
    state.points.push({ px, py, marker });
    renderList(); draw(); updatePreview();
    window.showToast?.(`Point ${state.points.length} added — drag its pin on the map`, 'info');
  }
  function addPointAt(px, py, lng, lat) {
    const marker = new maplibregl.Marker({ element: makeMarkerEl(state.points.length + 1), draggable: true })
      .setLngLat([lng, lat]).addTo(map);
    marker.on('drag', updatePreview);
    marker.on('dragend', updatePreview);
    state.points.push({ px, py, marker });
  }
  function removePoint(i) {
    const p = state.points[i]; if (!p) return;
    try { p.marker.remove(); } catch (e) {}
    state.points.splice(i, 1);
    renumber(); renderList(); draw(); updatePreview();
  }

  // Pre-load existing calibration points (image pixel + saved geo).
  if (existing && Array.isArray(existing.calibration)) {
    for (const p of existing.calibration) addPointAt(Number(p.px), Number(p.py), Number(p.lng), Number(p.lat));
    renumber(); renderList(); updatePreview();
  }

  // ── canvas zoom / pan / click ─────────────────────────────────────────────
  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const sc = canvas.width / rect.width;
    const cxp = (e.clientX - rect.left) * sc, cyp = (e.clientY - rect.top) * sc;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const ns = Math.max(0.05, Math.min(30, view.scale * factor));
    view.ox = cxp - (cxp - view.ox) * (ns / view.scale);
    view.oy = cyp - (cyp - view.oy) * (ns / view.scale);
    view.scale = ns; draw();
  }, { passive: false });

  let down = null;
  canvas.addEventListener('mousedown', (e) => { down = { x: e.clientX, y: e.clientY, ox: view.ox, oy: view.oy, moved: false }; });
  const onMove = (e) => {
    if (!down) return;
    const dx = e.clientX - down.x, dy = e.clientY - down.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) down.moved = true;
    if (down.moved) { const rect = canvas.getBoundingClientRect(); const sc = canvas.width / rect.width; view.ox = down.ox + dx * sc; view.oy = down.oy + dy * sc; draw(); }
  };
  const onUp = (e) => {
    if (!down) return; const wasClick = !down.moved; down = null;
    if (!wasClick) return;
    const rect = canvas.getBoundingClientRect(); const sc = canvas.width / rect.width;
    const cxp = (e.clientX - rect.left) * sc, cyp = (e.clientY - rect.top) * sc;
    const px = (cxp - view.ox) / view.scale, py = (cyp - view.oy) / view.scale;
    if (px >= 0 && py >= 0 && px <= width && py <= height) addPoint(px, py);
  };
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);

  panel.querySelector('#ovc-fit').addEventListener('click', () => { fit(); draw(); });
  panel.querySelector('#ovc-op').addEventListener('input', (e) => {
    state.opacity = Number(e.target.value);
    try { if (map.getLayer(PREVIEW_LAYER)) map.setPaintProperty(PREVIEW_LAYER, 'raster-opacity', state.opacity); } catch (er) {}
  });

  draw(); renderList();

  // ── teardown + commit ─────────────────────────────────────────────────────
  function cleanup(restoreOverlay) {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    state.points.forEach(p => { try { p.marker.remove(); } catch (e) {} });
    try { if (map.getLayer(PREVIEW_LAYER)) map.removeLayer(PREVIEW_LAYER); } catch (e) {}
    try { if (map.getSource(PREVIEW_SRC)) map.removeSource(PREVIEW_SRC); } catch (e) {}
    if (editingId && restoreOverlay) { try { map.setLayoutProperty(`poi-ov-${editingId}`, 'visibility', 'visible'); } catch (e) {} }
    panel.remove();
  }
  const cancel = () => { cleanup(true); show?.(); onDone?.(null); };
  panel.querySelector('#ovc-x').addEventListener('click', cancel);
  panel.querySelector('#ovc-cancel').addEventListener('click', cancel);

  doneBtn.addEventListener('click', async () => {
    if (state.points.length < 3) { window.showToast?.('Add at least 3 calibration points', 'warning'); return; }
    const pts = state.points.map(p => { const ll = p.marker.getLngLat(); return { px: p.px, py: p.py, lng: ll.lng, lat: ll.lat }; });
    const af = affineFromPoints(pts);
    if (!af) { window.showToast?.('Points are collinear — spread them out', 'warning'); return; }
    doneBtn.disabled = true; doneBtn.textContent = 'Saving…';
    try {
      let key = imageKey;
      if (!key) {
        const base64 = String(dataURL).split(',')[1];
        const r = await uploadOverlayImage(poiId, { dataBase64: base64, contentType });
        key = r.key;
      }
      const overlay = {
        id: editingId || `ov_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e4)}`,
        name: (nameInput.value || 'Overlay').trim() || 'Overlay',
        imageKey: key, width, height,
        calibration: pts, corners: cornersFromAffine(af, width, height),
        opacityDefault: state.opacity,
      };
      cleanup(false);      // the re-render recreates the real overlay from saved data
      show?.();
      onDone?.(overlay);
    } catch (e) {
      console.error('[overlay] save failed', e);
      window.showToast?.(`Save failed: ${e.message}`, 'danger');
      doneBtn.disabled = false; doneBtn.textContent = 'Save';
    }
  });
}
